import { useEffect } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useDispatch, useSelector } from 'react-redux';
import { setUser } from '@/redux/slices/authSlice';
import { authService } from '@/services/auth.service';
import type { AppDispatch, RootState } from '@/redux';

/**
 * ADR-012 §Frontend contract: "FE gọi /auth/me sau khi nhận WebSocket
 * `auth.context.invalidated`". Trước hook này, nửa WS của contract chưa từng được cài
 * đặt — đây là root cause của bug "set quyền cho role xong không thấy hiệu lực ngay":
 * `user.permissions` trong Redux chỉ được hydrate 1 lần lúc bootstrap
 * (`useAuthBootstrap`), không có tín hiệu nào báo cần refetch lại trong suốt phiên.
 *
 * Mount 1 lần ở App (sau khi authenticated), cạnh `useAuthBootstrap`.
 */
export function useAuthContextSocket(): void {
  const dispatch = useDispatch<AppDispatch>();
  const tenantId = useSelector((s: RootState) => s.auth.user?.tenantId);
  const accessToken = useSelector((s: RootState) => s.auth.accessToken);

  useEffect(() => {
    if (!tenantId || !accessToken) return;
    const wsUrl = import.meta.env.VITE_WS_URL;
    if (!wsUrl) return;

    const socket: Socket = io(`${wsUrl}/auth-ctx`, {
      transports: ['websocket'],
      auth: { token: accessToken },
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    socket.on('auth.context.invalidated', () => {
      authService
        .me()
        .then((u) => dispatch(setUser(u)))
        .catch(() => {
          // Best-effort — /auth/me tiếp theo (vd lần F5 kế tiếp) sẽ tự lấy giá trị mới.
        });
    });

    socket.on('connect_error', (err) => {
      if (import.meta.env.DEV) {
        console.debug('[useAuthContextSocket] connect_error:', err.message);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [tenantId, accessToken, dispatch]);
}
